/**
 * <place-picker> — birth place, with the resolved historical offset shown.
 *
 * ◆ **The picker shows the UTC offset that was actually in force at that place on
 * that date.** A 1943 Pune birth displaying +06:30, and a 1890 one displaying
 * +05:21:10, is the UI teaching something true — and it is the only way a user
 * can catch a wrong date or a wrong city before the chart is computed. Every
 * other chart tool shows +05:30 and says nothing.
 *
 * ⚠ Manual latitude/longitude entry is always available and is the **only fully
 * offline path**. It is not a fallback hidden behind a failed search: GeoNames
 * records population 0 for 550,854 of the 557,995 populated places it lists in
 * India, so a village birthplace frequently is not in any table, and the picker
 * says so plainly instead of showing an empty result.
 *
 * Emits `place-change` with `{ place, offsetMinutes, offsetSource, ambiguous,
 * skipped, instant }`.
 *
 * ⚠ **The event is named `place-change`, not `change`, and that is load-bearing.**
 * The plain `change` events fired by the inner `<input>` and `<select>` elements
 * bubble out of this component, so a listener on the host cannot tell them from
 * the component's own. Worse, they arrive *after* it: the native event reaches
 * the inner handler first, which re-renders and dispatches the real event, and
 * only then does the original keep bubbling — so the page received a good detail
 * and then immediately an `undefined` one, and the last write won. The symptom
 * was a chart that silently never computed.
 *
 * ◆ A distinct name cannot be shadowed by a native one, and it makes the page
 * code say which event it means.
 *
 * © Manish Jagdish Thatte
 */

import { manualPlace, search } from '../utils/geocode.js';
import { lmtOffset, localToUTC, looksLikeLMT, offsetAt } from '../utils/timezone.js';
import { formatLatitude, formatLongitude, formatOffset } from '../utils/format.js';

export class PlacePicker extends HTMLElement {
  connectedCallback() {
    if (this._built) return;
    this._built = true;
    this._mode = 'search';
    this._query = '';
    this._results = [];
    this._hint = null;
    this._place = null;
    this._searchAll = false;
    this._manual = { lat: '', lonEast: '', timezone: '' };
    this.render();
  }

  /** The local wall-clock ISO the offset is resolved *for*. Set by the page. */
  set forLocalISO(value) { this._localISO = value; this.renderOffset(); }
  get place() { return this._place; }

  render() {
    // ⚠ Per-document ids: a page with two pickers sets `data-id-prefix` on each.
    idPrefix = this.dataset.idPrefix ?? '';
    this.textContent = '';
    const fs = document.createElement('fieldset');
    fs.className = 'field-group';
    const legend = document.createElement('legend');
    legend.textContent = 'Birth place';
    fs.append(legend);

    const tabs = document.createElement('div');
    tabs.className = 'tabs';
    for (const [mode, label] of [['search', 'Search'], ['manual', 'Enter coordinates']]) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'tab';
      b.textContent = label;
      b.setAttribute('aria-pressed', String(this._mode === mode));
      b.addEventListener('click', () => { this._mode = mode; this.render(); });
      tabs.append(b);
    }
    fs.append(tabs);

    if (this._mode === 'search') this.renderSearch(fs);
    else this.renderManual(fs);

    this._offsetSlot = document.createElement('div');
    this._offsetSlot.className = 'place-picker__offset';
    fs.append(this._offsetSlot);

    this.append(fs);
    this.renderOffset();
  }

  renderSearch(fs) {
    const wrap = document.createElement('div');
    wrap.className = 'field';
    const label = document.createElement('label');
    label.setAttribute('for', `${idPrefix}pp-q`);
    label.textContent = 'Town or city';
    const input = document.createElement('input');
    input.type = 'search';
    input.id = `${idPrefix}pp-q`;
    input.value = this._query;
    input.autocomplete = 'off';
    input.placeholder = 'Porbandar, Allahabad, Bombay…';
    input.addEventListener('input', () => this.runSearch(input.value));
    wrap.append(label, input);
    fs.append(wrap);

    fs.append(note(
      'Historical names work: Allahabad, Bombay, Calcutta, Madras, Poona, Benares.'
    ));

    const list = document.createElement('ul');
    list.className = 'place-picker__results';
    for (const r of this._results) {
      const li = document.createElement('li');
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'place-picker__result';
      // ⚠ When the hit came from a historical name, say which name matched.
      // "Bombay → Mumbai" tells the user the lookup understood them; showing
      // only "Mumbai" looks like it ignored what they typed.
      const via = r.via === 'alternate' && r.matchedAs.toLowerCase() !== r.name.toLowerCase()
        ? ` — matched “${r.matchedAs}”` : '';
      b.textContent = `${r.name}${r.admin1 ? `, ${r.admin1}` : ''}, ${r.country}${via}`;
      b.addEventListener('click', () => this.choose(r));
      li.append(b);
      list.append(li);
    }
    if (this._results.length) fs.append(list);

    if (this._hint) {
      const box = document.createElement('div');
      box.className = 'notice notice--info';
      const p = document.createElement('p');
      p.textContent =
        'No match in the places loaded so far. Many Indian villages are not in any ' +
        'public gazetteer with usable coordinates, so this is common and not a failure — ' +
        'entering the latitude and longitude gives exactly the same chart.';
      box.append(p);
      if (this._hint.canWidenSearch && !this._searchAll) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'button button--quiet';
        b.textContent = 'Search the rest of the world (about 580 KB)';
        b.addEventListener('click', () => { this._searchAll = true; this.runSearch(this._query); });
        box.append(b);
      }
      const b2 = document.createElement('button');
      b2.type = 'button';
      b2.className = 'button button--quiet';
      b2.textContent = 'Enter coordinates instead';
      b2.addEventListener('click', () => { this._mode = 'manual'; this.render(); });
      box.append(b2);
      fs.append(box);
    }
  }

  renderManual(fs) {
    fs.append(textField('Latitude (north positive)', 'lat', this._manual.lat,
      '21.6422', (v) => { this._manual.lat = v; this.acceptManual(); }));
    // ⚠ Labelled east-positive at the point of entry. A user who types 73.85 for
    // a US city gets a chart mirrored across the globe, and there is nothing in
    // the output that would look wrong.
    fs.append(textField('Longitude (east positive, west negative)', 'lon', this._manual.lonEast,
      '69.6093', (v) => { this._manual.lonEast = v; this.acceptManual(); }));
    fs.append(textField('IANA time zone (optional)', 'tz', this._manual.timezone,
      'Asia/Kolkata', (v) => { this._manual.timezone = v; this.acceptManual(); }));
    fs.append(note(
      'Without a time zone the offset is taken as Local Mean Time for the longitude, ' +
      'which is usually the right reading for a birth before standard time reached that place.'
    ));
  }

  async runSearch(query) {
    this._query = query;
    const { results, missingHint } = await search(query, {
      chunks: this._searchAll ? 'all' : ['in']
    });
    this._results = results;
    this._hint = missingHint;
    this.render();
    const input = this.querySelector('input[type="search"]');
    if (input) { input.focus(); input.setSelectionRange(query.length, query.length); }
  }

  acceptManual() {
    const lat = Number(this._manual.lat);
    const lonEast = Number(this._manual.lonEast);
    if (!Number.isFinite(lat) || !Number.isFinite(lonEast)) return;
    try {
      this.choose(manualPlace({
        lat, lonEast, timezone: this._manual.timezone.trim() || null
      }), { rerender: false });
    } catch {
      // Out of range; the offset panel stays empty rather than showing nonsense.
    }
  }

  choose(place, { rerender = true } = {}) {
    this._place = place;
    if (rerender) this.render(); else this.renderOffset();
    this.emit();
  }

  /**
   * The heart of this component: resolve and display the offset for THIS date.
   */
  renderOffset() {
    if (!this._offsetSlot) return;
    this._offsetSlot.textContent = '';
    const place = this._place;
    if (!place) return;

    const head = document.createElement('p');
    head.className = 'place-picker__chosen';
    head.textContent =
      `${place.name} · ${formatLatitude(place.lat)} · ${formatLongitude(place.lonEast)}`;
    this._offsetSlot.append(head);

    const resolved = this.resolveOffset(place);
    if (!resolved) return;

    const p = document.createElement('p');
    p.className = 'place-picker__offset-value';
    p.innerHTML = '';
    const strong = document.createElement('strong');
    strong.textContent = formatOffset(resolved.offsetMinutes);
    p.append('UTC offset on this date: ', strong);
    this._offsetSlot.append(p);

    const why = document.createElement('p');
    why.className = 'field-note';
    why.textContent = resolved.explanation;
    this._offsetSlot.append(why);

    // ⚠ A DST fold gives two real instants and a gap gives none. Both are shown,
    // never silently resolved — an hour is about 15° of lagna.
    if (resolved.ambiguous) {
      this._offsetSlot.append(warn(
        'This local time happened twice on this date, when the clocks went back. ' +
        'The earlier of the two is used. If the birth was after the change, the chart ' +
        'is an hour out — about half a rāśi of lagna.'
      ));
    }
    if (resolved.skipped) {
      this._offsetSlot.append(warn(
        'This local time did not occur on this date — the clocks jumped forward past it. ' +
        'Check the recorded time.'
      ));
    }

    // ⚠ When both readings are defensible, both are offered. Choosing silently
    // would hide a difference worth most of a rāśi of lagna.
    if (resolved.alternative) {
      const box = document.createElement('div');
      box.className = 'notice notice--info';
      const p = document.createElement('p');
      p.textContent = resolved.autoChoseLMT
        ? 'This is before standard time reached the region, so the town\u2019s own mean ' +
          'time is used. The other defensible reading is the zone\u2019s historical ' +
          `offset, ${formatOffset(resolved.alternative.offsetMinutes)}.`
        : `The other defensible reading is ${formatOffset(resolved.alternative.offsetMinutes)}.`;
      box.append(p);

      const row = document.createElement('div');
      row.className = 'button-row';
      for (const [value, label] of [
        ['lmt', `Use the town\u2019s mean time`],
        ['zone', `Use the zone\u2019s offset`]
      ]) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'button button--quiet';
        b.textContent = label;
        b.setAttribute('aria-pressed',
          String((this._offsetPreference ?? 'auto') === value
            || (this._offsetPreference == null && value === (resolved.source === 'lmt' ? 'lmt' : 'zone'))));
        b.addEventListener('click', () => {
          this._offsetPreference = value;
          this.renderOffset();
          this.emit();
        });
        row.append(b);
      }
      box.append(row);
      this._offsetSlot.append(box);
    }
  }

  /**
   * ⚠ **For a pre-standard-time date the IANA zone is the WRONG default, and
   * this is the most consequential judgement in the component.**
   *
   * tzdata's pre-1906 `Asia/Kolkata` entries record what clocks in *Calcutta*
   * (later Madras) read — +05:53:20 until 1870, then +05:21:10. They do not
   * describe Porbandar, 1,500 km west, where a clock in 1869 was set by the
   * local sun and read roughly +04:38. Applying the zone's value to a Gujarat
   * birth is a 75-minute error, which is about 19° of lagna — most of a rāśi.
   *
   * ◆ Measured, for Gandhi's 2 October 1869 07:11 at Porbandar:
   *
   *     Asia/Kolkata for that date  +05:53:20  →  lagna Kanyā 17°06′
   *     Porbandar's own LMT         +04:38:26  →  lagna Tulā  4°24′
   *
   * ◆ The tie-break is citable rather than a preference. JHora — the classical own
   * software — ships a Mahatma Gandhi chart, and its `.jhd` stores the UTC
   * offset as **−4.654444 h**, which is LMT for 69°49′ E. It is not the zone
   * offset. So LMT is the convention practitioners actually use for such a
   * birth.
   *
   * So: when the zone is still in its own LMT era on that date — which
   * `looksLikeLMT` detects by the offset having fractional minutes — the
   * **place's own** local mean time is the default, and the zone's value is
   * offered beside it. After standard time reaches the zone the two questions
   * collapse and the zone is simply correct.
   */
  resolveOffset(place) {
    const localISO = this._localISO;
    if (!localISO) return null;

    const lmtMinutes = lmtOffset(place.lonEast);
    const lmtAnswer = {
      offsetMinutes: lmtMinutes,
      ambiguous: false,
      skipped: false,
      instant: null,
      source: 'lmt',
      explanation:
        `Local Mean Time for ${formatLongitude(place.lonEast)} (longitude ÷ 15°/h). ` +
        'Before standard time reached a town, its clocks were set by the local sun, ' +
        'so this is the reading a record of that date means — not a fallback.'
    };

    if (!place.timezone) return lmtAnswer;

    let zoneAnswer = null;
    try {
      const r = localToUTC(localISO, place.timezone);
      const lmtEra = looksLikeLMT(place.timezone, r.instant);
      zoneAnswer = {
        offsetMinutes: r.offsetMinutes,
        ambiguous: r.ambiguous,
        skipped: r.skipped,
        instant: r.instant,
        source: lmtEra ? 'tzdata-lmt-era' : 'tzdata',
        lmtEra,
        explanation: lmtEra
          ? `From the IANA database for ${place.timezone}. ⚠ On this date the zone ` +
            'itself was still on one city\u2019s mean time — Calcutta, then Madras, for ' +
            'India — which is not the time a clock in this town would have shown.'
          : `From the IANA database for ${place.timezone}, for this date — not today\u2019s offset.`
      };
    } catch {
      // An unknown zone name falls through to LMT rather than defaulting to UTC.
      return lmtAnswer;
    }

    const preference = this._offsetPreference ?? 'auto';
    if (preference === 'zone') return { ...zoneAnswer, alternative: lmtAnswer };
    if (preference === 'lmt') return { ...lmtAnswer, alternative: zoneAnswer };
    // Auto: the place's own LMT while the zone is still in its LMT era.
    return zoneAnswer.lmtEra
      ? { ...lmtAnswer, alternative: zoneAnswer, autoChoseLMT: true }
      : { ...zoneAnswer, alternative: null };
  }

  emit() {
    const resolved = this._place ? this.resolveOffset(this._place) : null;
    this.dispatchEvent(new CustomEvent('place-change', {
      detail: {
        place: this._place,
        offsetMinutes: resolved?.offsetMinutes ?? null,
        offsetSource: resolved?.source ?? null,
        ambiguous: resolved?.ambiguous ?? false,
        skipped: resolved?.skipped ?? false,
        instant: resolved?.instant ?? null
      },
      bubbles: true
    }));
  }
}

/** Set by render() for the instance being built; helpers below read it. */
let idPrefix = '';

function textField(labelText, id, value, placeholder, onChange) {
  const wrap = document.createElement('div');
  wrap.className = 'field';
  const label = document.createElement('label');
  label.setAttribute('for', `${idPrefix}pp-${id}`);
  label.textContent = labelText;
  const input = document.createElement('input');
  input.type = 'text';
  input.id = `${idPrefix}pp-${id}`;
  input.value = value;
  input.placeholder = placeholder;
  input.inputMode = id === 'tz' ? 'text' : 'decimal';
  input.addEventListener('change', () => onChange(input.value));
  wrap.append(label, input);
  return wrap;
}

function note(text) {
  const p = document.createElement('p');
  p.className = 'field-note';
  p.textContent = text;
  return p;
}

function warn(text) {
  const p = document.createElement('p');
  p.className = 'notice notice--warning';
  p.textContent = text;
  return p;
}

customElements.define('place-picker', PlacePicker);
